// src/components/Auth/BiometricPrompt.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Fingerprint as FingerprintIcon,
  Face as FaceIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const BiometricPrompt = ({ open, onEnable, onSkip, biometryType, loading }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const getIcon = () => {
    switch (biometryType) {
      case 'face': return <FaceIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />;
      case 'iris': return <SecurityIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />;
      default: return <FingerprintIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />;
    }
  };

  const getBiometricName = () => {
    switch (biometryType) {
      case 'face': return t('biometric.faceId', 'Face Recognition');
      case 'iris': return t('biometric.iris', 'Iris Scan');
      default: return t('biometric.fingerprint', 'Fingerprint');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onSkip}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.04)} 100%)`,
          pt: 4,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            mb: 2,
          }}
        >
          {getIcon()}
        </Box>
      </Box>

      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Typography variant="h6" fontWeight={700}>
          {t('biometric.enableTitle', 'Enable Quick Login?')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 3, pb: 1 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {t('biometric.enableDescription', 'Use {{type}} to sign in instantly next time — no password needed.', { type: getBiometricName() })}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {t('biometric.securityNote', 'Your credentials are stored securely in your device\'s hardware vault.')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onEnable}
          disabled={loading}
          startIcon={biometryType === 'face' ? <FaceIcon /> : <FingerprintIcon />}
          sx={{
            borderRadius: 2,
            height: 48,
            background: 'linear-gradient(90deg, #4b6cb7 0%, #182848 100%)',
            boxShadow: '0 4px 15px rgba(75, 108, 183, 0.35)',
          }}
        >
          {loading
            ? t('biometric.enabling', 'Setting up...')
            : t('biometric.enable', 'Enable {{type}}', { type: getBiometricName() })
          }
        </Button>
        <Button
          variant="text"
          fullWidth
          onClick={onSkip}
          disabled={loading}
          sx={{ color: 'text.secondary' }}
        >
          {t('biometric.skip', 'Maybe Later')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BiometricPrompt;
